import { Component, Input, Output, OnInit, ContentChild, TemplateRef, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd';

import { DataSourceService } from '../../../services/service/data-source.service';
import { UserContextService } from '../../../services/service/user-context.service';
import { TreeView, TreeNode } from '../../model/tree-view';

@Component({
  selector: 'zx-tree-view',
  templateUrl: './tree-view.component.html',
  styleUrls: ['./tree-view.component.css']
})
export class TreeViewComponent implements OnInit {
  private _initReady: boolean = false;
  private _stateKey;
  private _initFromState: boolean = false;

  private _isCollapsed: boolean = false;
  private _treeView: TreeView;
  private _data: any;
  private _dataUrl: string;
  private _activeOnClick: boolean = true;
  private _accordion: boolean = true;

  @Input() headerText: string;
  @Input() headerIcon: string;
  @Input() nonDataText: string;
  @Input() enableHeader: boolean = false;
  @Input() enableHeaderClick: boolean = true;
  @Input() deselectOnHeaderClick: boolean = true;
  @Input() enableCheckbox: boolean = false;
  @Input() disableTreeNodeAction: boolean = true;
  @Input() theme: string = 'light';
  @Input() loadChildrenHandler: Function;
  @Input() enableStateCache: boolean = true; // 启用状态缓存，返回页面时状态自动还原
  @Input() stateCacheKey: string;


  @ContentChild('treeViewHeader', {static:true}) tpl_header: TemplateRef<void>;
  @ContentChild('treeViewItem', {static:true}) tpl_itemTemplate: TemplateRef<void>;

  @Output() headerClicked: EventEmitter<any> = new EventEmitter();
  @Output() treeNodeClick: EventEmitter<any> = new EventEmitter();
  @Output() dataLoaded: EventEmitter<any> = new EventEmitter();
  @Output() selectChange: EventEmitter<TreeNode> = new EventEmitter();
  @Output() checkedChange: EventEmitter<TreeNode> = new EventEmitter();
  @Output() inited: EventEmitter<TreeView> = new EventEmitter();

  constructor(
    private router: Router,
    private dataSourceService: DataSourceService,
    private userContext: UserContextService,
    private message: NzMessageService,
  ) {
  }

  ngOnInit() {
    this._initReady = true;

    this._stateKey = "treeview-state:" + (this.stateCacheKey || '') + "[" + this.router.url + "][" + (this._dataUrl || '') + "]";
    // console.log(this._stateKey);

    let treeView;
    if (this.enableStateCache) {
      treeView = this.userContext.getData(this._stateKey);
      if (treeView != null) {
        // console.log("treeView from state cache: ", treeView);
        this._initFromState = true;
      }
    }
    if (treeView == null) treeView = new TreeView();
    treeView.enableCheckbox = this.enableCheckbox;
    treeView.accordion = this.accordion;
    treeView.activeOnClick = this.activeOnClick;
    treeView.nonDataText = this.nonDataText;
    treeView.loadChildrenHandler = this.loadChildrenHandler ? this.loadChildrenHandler : this.defaultLoadChildrenHandler;
    treeView.selectChange.subscribe(event => { this.saveState(); this.selectChange.emit(event); });
    treeView.checkedChange.subscribe(event => { this.saveState(); this.checkedChange.emit(event); });
    treeView.dataLoaded.subscribe(event => { this.dataLoaded.emit(event); this.saveState(); });

    this._treeView = treeView;

    this.initTreeView();
  }

  private initTreeView() {
    if (!this._initReady) return;

    if (this._data == null) {
      if (this._dataUrl != null) this.treeView.dataUrl = this._dataUrl;
      if (this._initFromState) this.reloadLoadChildren();
      else this.treeView.loadData();
    } else {
      if (this._initFromState) this.reloadLoadChildren();
      else this.treeView.loadData(this._data);
    }
    //if(this._initFromState) this.treeView.selectChange.emit(this.treeView.getSelectedNode());
    this.inited.emit(this.treeView);
  }
  private saveState() {
    // console.log("treeView save state: ", this._stateKey, this.treeView);
    this.userContext.putData(this._stateKey, this.treeView);
  }

  
  // @HostListener('document:mouseup',['$event']) onMouseup(event) {
  //   console.log(event);
  // }


  @Input() set accordion(v: boolean) {
    this._accordion = v;
    if (this._treeView) this._treeView.accordion = v;
  }
  get accordion(): boolean {
    return this._accordion;
  }

  @Input() set activeOnClick(v: boolean) {
    this._activeOnClick = v;
    if (this._treeView) this._treeView.activeOnClick = v;
  }
  get activeOnClick(): boolean {
    return this._activeOnClick;
  }

  @Input() set data(v: any) {
    if (this._data == v) return;

    this._data = v;
    this.initTreeView();
  }
  get data(): any {
    return this._data;
  }

  @Input() set dataUrl(v: string) {
    if (this._dataUrl == v) return;

    this._dataUrl = v;
    this.initTreeView();
  }
  get dataUrl(): string {
    return this._dataUrl;
  }

  @Input() set isCollapsed(v: boolean) {
    if (this._isCollapsed == v) return;

    this._isCollapsed = v;
    if (!v) setTimeout(() => this.treeView.openToSelected(), 100);
  }
  get isCollapsed(): boolean {
    return this._isCollapsed;
  }

  private get defaultLoadChildrenHandler(): Function {
    return ((self) => {
      return (treeNode: TreeNode) => {
        // console.log("defaultLoadChildrenHandler before load:", treeNode);
        if (treeNode.childrenUrl) {
          treeNode.removeAllChildren();
          let dataSource = self.dataSourceService.getDataSource(treeNode.childrenUrl);
          dataSource.query().subscribe(response => {
            const data = response.body;
            treeNode.loadChildren(data);
            // console.log("defaultLoadChildrenHandler loaded:",self.treeView);
          }, error => {
            // console.log(error);
            treeNode.childrenLoadError = error;
          });
        }else{
          treeNode.childrenLoading = false;
        }
      };
    })(this);
  }

  public get treeView(): TreeView {
    return this._treeView;
  }

  public reloadLoadChildren() {
    this._treeView.loadChildren(this._treeView.getSelectedNode());
  }

  public headerClick() {
    if (this.deselectOnHeaderClick) this._treeView.select(null);
    if (this.enableHeaderClick) this.headerClicked.emit(this.treeView);
  }

  onTreeNodeClick(treeNode, event:any){
    this.treeView.select(treeNode,event);
    this.treeNodeClick.emit(treeNode);
  }

  onDrag(treeNode, event:any){
    // console.log(event);
    if(treeNode.draggable&&treeNode.draggable.enableDrag&&treeNode.draggable.drag) 
      try{treeNode.draggable.drag.emit(event);}catch{}
  }
  onDrop(treeNode, event:any){
    try{event.el.parentNode.removeChild(event.el);}catch{} // 删除创建的元素，由定义事件自行处理
    event.targetTreeNode = treeNode;
    event.sourceTreeNode = event.value;
    // console.log(event);
    if(treeNode.draggable&&treeNode.draggable.enableDrop&&treeNode.draggable.drop) 
      try{treeNode.draggable.drop.emit(event);}catch{}
  }
}